import { useState, useEffect, useMemo, useCallback } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, storageApiRef } from '@backstage/core-plugin-api';
import { grafanaQueryApiRef } from '../api/GrafanaQueryApi';
import type { TimeRange } from '../api/types';

const PRESET_STORAGE_KEY = 'grafana-dashboard/time-range-preset';
const CUSTOM_RANGE_STORAGE_KEY = 'grafana-dashboard/time-range-custom';

const TIME_RANGE_PRESETS: { label: string; value: TimeRange }[] = [
  { label: '1h', value: { from: 'now-1h', to: 'now' } },
  { label: '6h', value: { from: 'now-6h', to: 'now' } },
  { label: '24h', value: { from: 'now-24h', to: 'now' } },
  { label: '7d', value: { from: 'now-7d', to: 'now' } },
];

export interface EntityFilters {
  /** All discovered service_name values from Prometheus */
  services: string[];
  /** Currently selected service filter (regex pattern) */
  selectedService: string;
  setSelectedService: (svc: string) => void;
  timeRange: TimeRange;
  setTimeRangePreset: (label: string) => void;
  setCustomTimeRange: (range: TimeRange) => void;
  timeRangePresets: { label: string; value: TimeRange }[];
  activePreset: string | null;
}

/**
 * Derive Grafana query filters from the current catalog entity annotations
 * and fetch service_name options dynamically from Prometheus.
 */
export function useEntityFilters(): EntityFilters {
  const { entity } = useEntity();
  const api = useApi(grafanaQueryApiRef);
  const storageApi = useApi(storageApiRef);
  const bucket = useMemo(() => storageApi.forBucket('settings'), [storageApi]);

  const defaultService = useMemo(() => entity.metadata.name, [entity]);

  // Service dropdown state — default to entity-derived value
  const [services, setServices] = useState<string[]>([]);
  const [selectedService, setSelectedService] = useState<string>(defaultService);

  // Time range state — persisted via StorageApi
  const storedPreset = bucket.snapshot<string>(PRESET_STORAGE_KEY).value;
  const storedCustomRaw = bucket.snapshot<{ from: string; to: string }>(CUSTOM_RANGE_STORAGE_KEY).value;
  const storedCustom: TimeRange | null = storedCustomRaw
    ? { from: storedCustomRaw.from, to: storedCustomRaw.to }
    : null;

  const [activePreset, setActivePreset] = useState<string | null>(
    storedCustom ? null : (storedPreset ?? '1h'),
  );
  const [customRange, setCustomRange] = useState<TimeRange | null>(
    storedCustom ?? null,
  );
  const timeRange = useMemo(() => {
    if (customRange) return customRange;
    const preset = TIME_RANGE_PRESETS.find(p => p.label === activePreset);
    return preset?.value ?? TIME_RANGE_PRESETS[0].value;
  }, [activePreset, customRange]);

  const setTimeRangePreset = useCallback((label: string) => {
    setActivePreset(label);
    setCustomRange(null);
    bucket.set(PRESET_STORAGE_KEY, label);
    bucket.remove(CUSTOM_RANGE_STORAGE_KEY);
  }, [bucket]);

  const setCustomTimeRange = useCallback((range: TimeRange) => {
    setCustomRange(range);
    setActivePreset(null);
    bucket.set(CUSTOM_RANGE_STORAGE_KEY, { from: range.from, to: range.to });
    bucket.remove(PRESET_STORAGE_KEY);
  }, [bucket]);

  // Fetch available service_name values
  useEffect(() => {
    let cancelled = false;
    api
      .queryLabelValues('service_name')
      .then(vals => {
        if (!cancelled && vals.length) {
          setServices(vals.sort());
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api]);

  return {
    services,
    selectedService,
    setSelectedService,
    timeRange,
    setTimeRangePreset,
    setCustomTimeRange,
    timeRangePresets: TIME_RANGE_PRESETS,
    activePreset,
  };
}
